import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, MapPin, Gift, ChevronRight, HelpCircle, Package, Heart, MessageCircle, Home } from "lucide-react";

const TOP_CITIES = [
  { name: "București", slug: "bucuresti" },
  { name: "Cluj-Napoca", slug: "cluj-napoca" },
  { name: "Timișoara", slug: "timisoara" },
  { name: "Iași", slug: "iasi" },
  { name: "Brașov", slug: "brasov" },
  { name: "Constanța", slug: "constanta" },
  { name: "Craiova", slug: "craiova" },
  { name: "Galați", slug: "galati" },
  { name: "Ploiești", slug: "ploiesti" },
  { name: "Oradea", slug: "oradea" },
];

const OCCASIONS = [
  { name: "Ziua de naștere", slug: "ziua-de-nastere", emoji: "🎂" },
  { name: "Aniversare cuplu", slug: "aniversare", emoji: "💑" },
  { name: "Valentine's Day", slug: "valentine", emoji: "❤️" },
  { name: "8 Martie", slug: "8-martie", emoji: "🌷" },
  { name: "Crăciun", slug: "craciun", emoji: "🎄" },
  { name: "Botez", slug: "botez", emoji: "👶" },
];

const NavLink = ({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      className={cn(
        "text-sm transition-colors hover:text-foreground font-inter",
        active ? "text-foreground font-medium" : "text-muted-foreground",
        className
      )}
    >
      {children}
    </Link>
  );
};

const AppHeader = () => {
  const { user, hasRole, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Shadow la scroll
  if (typeof window !== "undefined") {
    window.addEventListener("scroll", () => {
      setScrolled(window.scrollY > 10);
    }, { passive: true });
  }

  return (
    <header className={cn(
      "border-b border-border/60 bg-background/95 backdrop-blur-sm sticky top-0 z-50 transition-shadow duration-300",
      scrolled && "shadow-warm"
    )}>
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">

        {/* Logo */}
        <Link to="/" className="shrink-0 flex items-baseline gap-1">
          <span className="font-playfair font-bold italic text-lg text-primary">Surprize</span>
          <span className="font-inter text-sm font-medium text-muted-foreground">la Domiciliu</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList className="gap-1">

              <NavigationMenuItem>
                <NavLink to="/cum-functioneaza" className="px-2 py-2 rounded-lg hover:bg-secondary flex items-center whitespace-nowrap">
                  <HelpCircle className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Cum funcționează
                </NavLink>
              </NavigationMenuItem>

              {/* Orașe dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-normal text-muted-foreground font-inter bg-transparent hover:bg-secondary data-[state=open]:bg-secondary h-9 px-2 rounded-lg">
                  <MapPin className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Orașe
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="w-[360px] p-4 bg-card border border-border/60 rounded-2xl shadow-warm">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 font-inter">
                      Orașe populare
                    </p>
                    <ul className="grid grid-cols-2 gap-1">
                      {TOP_CITIES.map((city) => (
                        <li key={city.slug}>
                          <Link
                            to={`/surprize-la-domiciliu/${city.slug}`}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-secondary hover:text-primary transition-colors font-inter"
                          >
                            <MapPin className="h-3 w-3 text-primary/60 shrink-0" />
                            {city.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-border/60 mt-3 pt-3">
                      <Link
                        to="/orase"
                        className="flex items-center gap-1 text-sm text-primary font-medium hover:underline font-inter"
                      >
                        Vezi toate orașele
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Ocazii dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-normal text-muted-foreground font-inter bg-transparent hover:bg-secondary data-[state=open]:bg-secondary h-9 px-2 rounded-lg">
                  <Gift className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Ocazii
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="w-[280px] p-4 bg-card border border-border/60 rounded-2xl shadow-warm">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 font-inter">
                      Alege ocazia
                    </p>
                    <ul className="space-y-1">
                      {OCCASIONS.map((occ) => (
                        <li key={occ.slug}>
                          <Link
                            to={`/ocazii/${occ.slug}`}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm hover:bg-secondary hover:text-primary transition-colors font-inter"
                          >
                            <span className="text-base">{occ.emoji}</span>
                            {occ.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavLink to="/pachete" className="px-2 py-2 rounded-lg hover:bg-secondary flex items-center whitespace-nowrap">
                  <Package className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Pachete
                </NavLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavLink to="/despre-noi" className="px-2 py-2 rounded-lg hover:bg-secondary flex items-center whitespace-nowrap">
                  <Heart className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Despre noi
                </NavLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavLink to="/contact" className="px-2 py-2 rounded-lg hover:bg-secondary flex items-center whitespace-nowrap">
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5 text-primary" />
                  Contact
                </NavLink>
              </NavigationMenuItem>

            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        {/* Desktop Right */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          {user ? (
            <>
              {hasRole("admin") && (
                <NavLink to="/admin" className="px-2 py-1.5 rounded-lg hover:bg-secondary">Admin</NavLink>
              )}
              {hasRole("partner") && (
                <NavLink to="/partener" className="px-2 py-1.5 rounded-lg hover:bg-secondary">Partener</NavLink>
              )}
              <NavLink to="/cont" className="px-2 py-1.5 rounded-lg hover:bg-secondary">Contul meu</NavLink>
              <button
                onClick={signOut}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-inter"
              >
                Ieși
              </button>
            </>
          ) : (
            <>
              <NavLink to="/autentificare" className="px-2 py-1.5 rounded-lg hover:bg-secondary">Autentificare</NavLink>
              <Button
                size="sm"
                className="bg-gradient-primary border-0 shadow-warm hover:opacity-90 transition-opacity font-inter"
                onClick={() => navigate("/comanda-surpriza")}
              >
                Trimite o surpriză →
              </Button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Deschide meniu"
                className="p-2 rounded-lg hover:bg-secondary transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 overflow-y-auto bg-background">
              <SheetHeader>
              <SheetTitle className="text-left">
                  <span className="font-playfair font-bold italic text-primary">Surprize</span>
                  <span className="font-inter text-sm font-medium text-muted-foreground"> la Domiciliu</span>
                </SheetTitle>
              </SheetHeader>

              <nav className="mt-6 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-1 font-inter">
                  Navigare
                </p>
                {[
                  { to: "/", label: "Acasă", icon: Home },
                  { to: "/cum-functioneaza", label: "Cum funcționează", icon: HelpCircle },
                  { to: "/pachete", label: "Pachete", icon: Package },
                  { to: "/despre-noi", label: "Despre noi", icon: Heart },
                  { to: "/contact", label: "Contact", icon: MessageCircle },
                  { to: "/orase", label: "Toate orașele", icon: MapPin },
                ].map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm hover:bg-secondary transition-colors font-inter"
                  >
                    <Icon className="h-3 w-3 text-primary/60" />
                    {label}
                  </Link>
                ))}

                <div className="pt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-1 font-inter">
                    Ocazii
                  </p>
                  {OCCASIONS.map((occ) => (
                    <Link
                      key={occ.slug}
                      to={`/ocazii/${occ.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors font-inter"
                    >
                      <span>{occ.emoji}</span>
                      {occ.name}
                    </Link>
                  ))}
                </div>

                <div className="pt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-1 font-inter">
                    Orașe populare
                  </p>
                  {TOP_CITIES.map((city) => (
                    <Link
                      key={city.slug}
                      to={`/surprize-la-domiciliu/${city.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors font-inter"
                    >
                      <MapPin className="h-3 w-3 text-primary/60" />
                      {city.name}
                    </Link>
                  ))}
                </div>

                <div className="border-t pt-4 mt-4 space-y-1">
                  {user ? (
                    <>
                      {hasRole("admin") && (
                        <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex px-3 py-2.5 rounded-lg text-sm hover:bg-secondary font-inter">Admin</Link>
                      )}
                      {hasRole("partner") && (
                        <Link to="/partener" onClick={() => setMobileOpen(false)} className="flex px-3 py-2.5 rounded-lg text-sm hover:bg-secondary font-inter">Partener</Link>
                      )}
                      <Link to="/cont" onClick={() => setMobileOpen(false)} className="flex px-3 py-2.5 rounded-lg text-sm hover:bg-secondary font-inter">Contul meu</Link>
                      <button
                        onClick={() => { signOut(); setMobileOpen(false); }}
                        className="flex w-full px-3 py-2.5 rounded-lg text-sm hover:bg-secondary text-muted-foreground font-inter"
                      >
                        Ieși din cont
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/autentificare" onClick={() => setMobileOpen(false)} className="flex px-3 py-2.5 rounded-lg text-sm hover:bg-secondary font-inter">
                        Autentificare
                      </Link>
                      <div className="px-3 pt-1">
                        <Button
                          className="w-full bg-gradient-primary border-0"
                          onClick={() => { navigate("/comanda-surpriza"); setMobileOpen(false); }}
                        >
                          Trimite o surpriză →
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
